import { useDispatch, useSelector } from 'react-redux';
import {
  addPurchaseProduct,
  clearFormErrors,
  clearPurchaseForm,
  deletePurchaseProduct,
  setFormErrors,
  setPurchaseForm,
  updatePurchaseField,
  updatePurchaseProduct,
  setPurchaseSaved,
  setPendingPurchaseCount,
  setPendingPurchaseList,
  setFilteredPurchaseList,
  setPageSize,
  setPurchaseLoaded,
  setPurchaseToViewLoaded,
  setIsSubmitting,
  clearFilteredPurchaseList,
  resetBasePurchaseForm,
  decreasePendingPurchaseCount,
  removePendingPurchaseById,
  setActiveDocumentType,
  setFinalizedPurchaseCount,
  setFinalizedPurchaseList,
  setPurchaseSettlementLoaded,
  updateIndividualPayments,
  clearIndividualPayments,
  setPaymentConditionCode,
  setCorrelativeRequested,

  setClosePdfModal,
  setOpenPdfModal,
  clearMeta,
  setMeta,
  setOpenDtePdfModal,
  setCloseDtePdfModal,
  clearDocumentNumberAndCorrelative,
} from '../../store/slices/purchases/basePurchaseSlice';
import { AppDispatch, RootState } from '../../store/store';
import { BaseProduct, IndividualPayments, PurchaseFormValues } from '../../types/purchases/BasePurchase';
import BasePurchaseService from '../../services/purchases/basePurchaseService';
import useAlerts from '../useAlerts';
import useLoading from '../useLoading';
import { useNavigate } from 'react-router-dom';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import { mapPurchaseToUpdateDTO } from '../../utils/mappers/mapPurchaseToUpdateDTO';
import { mapPurchaseDetailToFormValues } from '../../utils/mappers/purchaseMapper';
import { mapPurchaseResponseToFormValues } from '../../utils/mappers/purchases/mapPurchaseResponseToFormValues';
import ProductSettlementService from '../../services/purchases/ProductSettlementService';
import useApiErrorHandler from '../useApiErrorHandler';
import { mapPurchaseToFinalizeDTO } from '../../mappers/purchase/finalize/PurchaseFinalizeMappers';
import CorrelativeService from '../../services/document/CorrelativeService';
import { DOCUMENT_TYPE_CODES } from '../../constants/documentTypeCodes';

export const useBasePurchase = () => {
  const { show: showAlert } = useAlerts();
  const { openModal } = useModals();
  const confirm = useConfirmModal();
  const navigate = useNavigate();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const dispatch: AppDispatch = useDispatch();
  const { handle } = useApiErrorHandler();

  const {
    form,
    purchaseSaved,
    purchaseLoaded,
    purchaseToViewLoaded,
    purchaseSettlementLoaded,
    formErrors,
    pendingPurchaseCount,
    pendingPurchaseList,
    finalizedPurchaseCount,
    finalizedPurchaseList,
    filteredPurchaseList,
    pageSize,
    paginator,
    isSubmitting,
    activeDocumentType,
    correlativeRequested,
    paymentConditionCode,
    showPdfModal,
    showDtePdfModal,
  } = useSelector((state: RootState) => state.basePurchase);

  /**
 * Genera una nueva compra a partir del formulario proporcionado.
 *
 * 📝 Envia los datos del encabezado (formData) al backend para registrar una nueva compra.
 * ✅ Si la respuesta es exitosa (201), se actualiza el formulario global con los datos recibidos
 *    y se marca como "compra guardada", mostrando además un mensaje de éxito al usuario.
 * ❌ Si la respuesta no es exitosa, muestra una alerta de error indicando que no se pudo generar la compra.
 * 🔁 Siempre oculta el loader al finalizar, y captura cualquier excepción para mostrar errores inesperados.
 *
 * @param formData - Objeto con los datos del formulario (encabezado de compra).
 */
  const handlerGenerate = async (formData: any) => {
    // console.log(formData);
    try {
      showLoadingIndicator();
      const response = await BasePurchaseService.generate(formData);
      // console.log("handlerGenerate", response);
      const { title, message, type, data, statusCode } = response;

      if (statusCode === 201) {
        const formValues = mapPurchaseDetailToFormValues(data);
        dispatch(setPurchaseForm(formValues));
        dispatch(setActiveDocumentType(formValues.documentType));
        dispatch(setPurchaseSaved(true));

        openModal(type, {
          title,
          message,
        });
        handlerClearFilteredPurchaseList();
      } else {
        showAlert("error", "Error", "No se pudo generar la compra.");
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /**
* Actualiza el encabezado de una compra existente.
*
* ✏️ Envia los nuevos datos del encabezado (formData) al backend para actualizar la compra.
* 💾 Si la respuesta es exitosa, actualiza el formulario global y muestra un mensaje de éxito.
* ❌ En caso de error, muestra una alerta indicando el problema.
*
* @param id - ID de la compra que se desea actualizar.
* @param formData - Nuevos datos del encabezado de compra.
*/
  const handlerUpdatePurchaseHeader = async (
    id: number,
    formData: PurchaseFormValues
  ) => {
    const payload = mapPurchaseToUpdateDTO(formData);
    // console.log('🧾 Payload enviado:', payload);

    try {
      showLoadingIndicator();

      const response = await BasePurchaseService.updatePurchaseHeader(id, payload);
      // console.log("Actualizar encabezado", response)

      if (response.statusCode === 200) {
        const { data, title, message, type } = response;

        const mappedForm = mapPurchaseResponseToFormValues(data);

        dispatch(setPurchaseForm(mappedForm));
        dispatch(setActiveDocumentType(mappedForm.documentType));
        openModal(type, { title, message });
      } else {
        showAlert('error', 'Error', 'No se pudo actualizar la compra.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
      dispatch(setIsSubmitting(false)); // ✅ Rehabilita botón
    }
  };


  /**
 * Obtiene el número total de compras pendientes asociadas a la sucursal Casa Matriz (ID: 1).
 *
 * 🔄 Realiza una solicitud al backend para contar las compras pendientes.
 * ✅ Si la respuesta es exitosa, actualiza el contador global de compras pendientes.
 * ❌ Si hay un error o falla la solicitud, se muestra una alerta al usuario.
 * 🔁 Siempre oculta el loader al finalizar la operación.
 */
  const handlerGetPendingTotal = async () => {
    try {
      // showLoadingIndicator();
      const response = await BasePurchaseService.getPendingTotalCasaMatriz();

      if (response.data?.statusCode === 200) {
        const { data } = response.data;
        dispatch(setPendingPurchaseCount(data))
      } else {
        showAlert("error", "Error", "No se pudo obtener el total pendiente.");
      }
    } catch (error) {
      handle(error);
    } finally {
      // hideLoadingIndicator();
    }
  };

  /**
 * Obtiene la lista de compras pendientes asociadas a la sucursal Casa Matriz (ID: 1).
 *
 * 📦 Realiza una solicitud al backend para recuperar todas las compras en estado PENDING.
 * ✅ Si la respuesta es exitosa, actualiza la lista de compras pendientes en el estado global.
 * ❌ Si ocurre un error o falla la petición, se muestra una alerta informando al usuario.
 * 🔁 Siempre oculta el loader al finalizar la operación.
 */
  const handlerGetPendingPurchases = async () => {
    try {
      showLoadingIndicator();

      const response = await BasePurchaseService.getPendingPurchasesCasaMatriz();

      if (response.statusCode === 200) {
        const { data } = response;
        dispatch(setPendingPurchaseList(data));
      } else {
        showAlert("error", "Error", "No se pudo obtener las compras pendientes.");
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /**
 * Obtiene el número total de compras finalizadas en Casa Matriz, excluyendo notas.
 *
 * 🔄 Realiza una solicitud al backend para contar las compras finalizadas.
 * ✅ Si la respuesta es exitosa, actualiza el contador global de compras finalizadas.
 * ❌ Si hay un error o falla la solicitud, se muestra una alerta al usuario.
 * 🔁 Siempre oculta el loader al finalizar la operación.
 */
  const handlerGetFinalizedTotal = async () => {
    try {
      // showLoadingIndicator();
      const response = await BasePurchaseService.getFinalizedTotalCasaMatriz();
      // console.log(response)

      if (response.data?.statusCode === 200) {
        const { data } = response.data;
        dispatch(setFinalizedPurchaseCount(data));
      } else {
        showAlert("error", "Error", "No se pudo obtener el total finalizado.");
      }
    } catch (error) {
      handle(error);
    } finally {
      // hideLoadingIndicator();
    }
  };

  /**
 * Obtiene la lista de compras finalizadas en Casa Matriz, excluyendo notas.
 *
 * 📦 Realiza una solicitud al backend para recuperar todas las compras en estado FINALIZED.
 * ✅ Si la respuesta es exitosa, actualiza la lista de compras finalizadas en el estado global.
 * ❌ Si ocurre un error o falla la petición, se muestra una alerta informando al usuario.
 * 🔁 Siempre oculta el loader al finalizar la operación.
 */
  const handlerGetFinalizedPurchases = async () => {
    try {
      showLoadingIndicator();

      const response = await BasePurchaseService.getFinalizedPurchasesCasaMatriz();

      if (response.statusCode === 200) {
        const { data } = response;
        dispatch(setFinalizedPurchaseList(data));
      } else {
        showAlert("error", "Error", "No se pudo obtener las compras finalizadas.");
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
 * Finaliza la compra actual y actualiza su estado a "FINALIZED".
 *
 * 🧾 Convierte los datos del formulario (`form`) al DTO requerido por el backend.
 * 🔐 Muestra un modal de confirmación antes de continuar.
 * 📤 Si se confirma, envía la solicitud al backend para finalizar la compra.
 * ✅ Si la respuesta es exitosa, muestra una alerta y redirige al historial de compras.
 * ⚠️ Si ocurre un error con código 400 o 404, abre un modal con el mensaje de error detallado.
 * ❌ Para otros errores inesperados, muestra una alerta genérica.
 * 🔄 Al finalizar, siempre oculta el loader y habilita nuevamente los botones.
 */
  const handlerFinalizePurchase = async () => {
    confirm({
      title: '¿Finalizar compra?',
      message:
        'Recuerde que esta compra ya no podrá ser modificada ni eliminada. En caso de existir un error, únicamente podrá ser anulada, ya que esta será aplicada al inventario.',
      icon: 'info',
      confirmText: 'Finalizar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          dispatch(setIsSubmitting(true));
          showLoadingIndicator();

          const purchaseId = form.id!;
          const payload = mapPurchaseToFinalizeDTO(form);

          const response = await BasePurchaseService.finalizePurchase(purchaseId, payload);

          const { title, message, type, statusCode } = response;
          // console.log(data);

          if (statusCode === 200) {
            openModal(type, { title, message });
            handlerResetBasePurchase();

          } else {
            showAlert('error', 'Error', 'No se pudo finalizar la compra.');
          }
        } catch (error: any) {
          handle(error);
        } finally {
          hideLoadingIndicator();
          dispatch(setIsSubmitting(false));
        }
      },
    });
  };


  /**
 * Filtra y pagina las compras según los criterios proporcionados.
 *
 * 📤 Envía los filtros al backend para obtener solo las compras que cumplan con los criterios.
 * 📄 La respuesta incluye una lista paginada (`content`) y metainformación del paginador.
 * 🧠 Guarda la lista de compras filtradas en Redux junto con los datos del paginador.
 * ❌ Si no se obtiene una respuesta válida o hay un error, muestra una alerta correspondiente.
 *
 * @param filters - Objeto con los filtros de búsqueda (tipo de documento, proveedor, fechas, etc.)
 * @param page - Número de página actual (por defecto 0).
 * @param size - Tamaño de página (por defecto 10).
 * @param sort - Criterio de ordenamiento (por defecto 'id,desc'; el backend lo
 *               fuerza a id descendente, igual que el historial de ventas).
 */
  const handlerGetFilteredPurchases = async (
    filters: any,
    page = 0,
    size = 10,
    sort = 'id,desc'
  ) => {
    try {
      showLoadingIndicator();
      const response = await BasePurchaseService.getFilteredPurchases(filters, page, size, sort);
      // console.log(response?.content)

      if (response?.content) {
        dispatch(setFilteredPurchaseList(response));
      } else {
        showAlert('error', 'Error', 'No se pudieron obtener las compras filtradas.');
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
   * Cancela una compra que está en PENDING (estado DRAFT_CANCELLED).
   * Devuelve true si la cancelación fue exitosa. El refresco de listas y
   * contadores lo hace quien llama (la página tiene los filtros vigentes).
   */
  const handlerCancelPendingPurchase = async (
    id: number,
    reason?: string
  ): Promise<boolean> => {
    try {
      showLoadingIndicator();
      const response = await BasePurchaseService.cancelPendingPurchase(id, reason);
      const { statusCode, type, title, message } = response ?? {};

      if (statusCode === 200) {
        showAlert(type || 'success', title || 'Compra cancelada', message || 'La compra pendiente fue cancelada.');
        return true;
      }

      showAlert('error', title || 'Error', message || 'No se pudo cancelar la compra.');
      return false;
    } catch (error) {
      handle(error);
      return false;
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
 * Obtiene una compra por su ID para visualización en modo solo lectura.
 *
 * 🔍 Realiza una solicitud al backend para obtener el detalle completo de la compra.
 * 🔁 Mapea la respuesta al formato del formulario (`PurchaseFormValues`) para el frontend.
 * 🧠 Guarda los datos en el estado global mediante `setPurchaseForm`.
 * ✅ Activa el estado `purchaseToViewLoaded = true` para mostrar el modal de visualización.
 * ❌ Si ocurre un error, muestra alerta y redirige al historial.
 *
 * @param id - ID de la compra a visualizar.
 */
  const handlerViewPurchaseById = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await BasePurchaseService.getPurchaseById(id);
      // console.log(response);

      if (response?.statusCode === 200) {
        const { data } = response;

        const formValues = mapPurchaseDetailToFormValues(data);

        dispatch(setPurchaseForm(formValues));
        dispatch(setActiveDocumentType(formValues.documentType)); // Igual que en edición
        dispatch(setPurchaseToViewLoaded(true));
      } else {
        showAlert('error', 'Error', 'No se pudo obtener la compra.');
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };


  /**
 * Obtiene una compra por su ID y prepara el formulario para su edición.
 *
 * 🔸 Realiza una solicitud al backend para obtener el detalle completo de la compra (encabezado y productos).
 * 🔸 Si la respuesta es exitosa, mapea los datos al formato del formulario y los guarda en el estado global.
 * 🔸 Marca el formulario como cargado (`purchaseLoaded = true`).
 * 🔸 Muestra una alerta con el mensaje del backend.
 * 🔸 Redirige al formulario de edición `/purchase-register`.
 * 🔸 Si ocurre un error (compra no encontrada, error inesperado, etc.), muestra un mensaje y redirige al historial.
 *
 * @param id - ID de la compra que se desea editar.
 */
  const handlerEditPurchaseById = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await BasePurchaseService.getPurchaseById(id);
      console.log("Editar", response);

      if (response?.statusCode === 200) {
        const { title, message, type, data } = response;

        // ✅ Mapeo flexible según el tipo de compra
        const formValues = mapPurchaseDetailToFormValues(data);

        if (formValues.documentType.code === DOCUMENT_TYPE_CODES.EXCLUDED_SUBJECT) {
          dispatch(setCorrelativeRequested(true));
        }

        // ⬇️ Guarda en el store global
        dispatch(setPurchaseForm(formValues));
        dispatch(setActiveDocumentType(formValues.documentType));
        dispatch(setPurchaseLoaded(true));

        // dispatch(setCorrelativeRequested(true));

        openModal(type, { title, message });

        navigate('/purchase-register');
        handlerClearFilteredPurchaseList();
      } else {
        showAlert('error', 'Error', 'No se pudo obtener la compra.');
        navigate('/purchase-history');
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };



  /**
 * Carga una compra con progreso de asentamiento y la prepara para ser editada.
 * 
 * 🔹 Usa el servicio de asentamiento para obtener datos.
 * 🔹 Marca el estado global para indicar modo asentamiento.
 * 🔹 Redirige al formulario reutilizable.
 */
  const handlerSettlePurchaseProgressById = async (id: number) => {
    try {
      showLoadingIndicator();

      const response = await ProductSettlementService.getPurchaseWithProgress(id);
      // console.log('🔄 Datos para asentamiento:', response);

      if (response?.statusCode === 200) {
        const { title, message, type, data } = response;

        const formValues = mapPurchaseDetailToFormValues(data);

        if (formValues.documentType.code === DOCUMENT_TYPE_CODES.EXCLUDED_SUBJECT) {
          dispatch(setCorrelativeRequested(true));
        }

        dispatch(setPurchaseForm(formValues));
        dispatch(setActiveDocumentType(formValues.documentType));
        dispatch(setPurchaseSettlementLoaded(true));

        openModal(type, { title, message });
        navigate('/purchase-register');
        handlerClearFilteredPurchaseList();
      } else {
        showAlert('error', 'Error', 'No se pudo cargar la compra con asentamientos.');
      }
    } catch (error) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  /**
   * Solicita al backend un nuevo correlativo para una compra y actualiza
   * automáticamente los campos del formulario (`correlative` y `documentNumber`).
   *
   * ✅ Realiza una solicitud `GET` al endpoint `/api/document-correlatives/purchase/correlative`.
   * ✅ Si la respuesta es válida, actualiza el formulario con el ID y número del correlativo.
   * ✅ Marca el estado `correlativeRequested` como `true` para bloquear el input.
   * ❌ Muestra una alerta si falla la solicitud o los datos no son válidos.
   *
   * @async
   * @function handlerGetPurchaseCorrelative
   * @param {number} documentTypeId - ID del tipo de documento asociado a la compra.
   * @param {(field: keyof PurchaseFormValues, value: any) => void} updateField - Función para actualizar campos del formulario.
   * @param {AppDispatch} dispatch - Función `dispatch` de Redux para actualizar estado global.
   *
   * @example
   * await handlerGetPurchaseCorrelative(3, updateField, dispatch);
   */
  const handlerGetPurchaseCorrelative = async (
    documentTypeId: number,
    updateField: (field: keyof PurchaseFormValues, value: any) => void,
  ): Promise<void> => {
    try {
      showLoadingIndicator();

      const response = await CorrelativeService.getForPurchase(documentTypeId);
      const { statusCode, data } = response;

      if (statusCode === 200) {
        updateField('correlative', {
          id: data.id,
          number: data.number,
        });

        updateField('documentNumber', String(data.number));

        dispatch(setCorrelativeRequested(true));
      } else {
        showAlert('error', 'Error', 'No se pudo obtener el correlativo de compra.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };




  const handlerResetPurchaseFormOnly = () => {
    const hasData =
      form?.products?.length > 0 ||
      form?.supplier ||
      form?.documentType ||
      form?.documentNumber ||
      form?.purchaseDate;

    if (!hasData) return;

    confirm({
      title: '¿Limpiar formulario?',
      message:
        'Se perderán los datos ingresados.\n\n¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Sí, limpiar',
      cancelText: 'Cancelar',
      onConfirm: () => {
        dispatch(resetBasePurchaseForm());
      },
    });
  };




  /**
  * Cambia el tamaño de la paginación del historial de compras.
  *
  * 🔹 Este método actualiza el número de registros que se mostrarán por página.
  * 🔹 Se puede utilizar desde un selector de cantidad de resultados por página.
  *
  * @param size - Número de elementos por página (ej. 10, 25, 50).
  */
  const updatePageSize = (size: number) => {
    dispatch(setPageSize(size));
  };


  /**
   * Cierra el modal de vista de compra y limpia el formulario.
   *
   * 🔹 Este método se usa cuando el usuario cierra el modal de "Ver Compra".
   * 🔹 Además de ocultar el modal, también limpia el formulario del estado global.
   * 🔹 Asegura que al volver a abrir otro modal, no se muestren datos anteriores.
   */
  const handleCloseViewModal = () => {
    dispatch(setPurchaseToViewLoaded(false)); // Oculta el modal
    dispatch(clearPurchaseForm());            // Limpia los datos del formulario
  };

  /**
  * Limpia la lista de compras filtradas y su paginador asociado.
  *
  * 🔸 Útil cuando el usuario cambia de vista, restablece filtros
  * o se desea reiniciar el listado de compras desde cero.
  *
  * Ejemplo de uso:
  *   - Al salir del historial de compras.
  *   - Al hacer clic en "Limpiar Filtros".
  *   - Después de cancelar o finalizar una compra, si se desea refrescar desde cero.
  */
  const handlerClearFilteredPurchaseList = () => {
    dispatch(clearFilteredPurchaseList());
  };


  /**
 * 🔄 Reinicia completamente el estado del formulario de compras.
 *
 * 🧹 Llama al reducer `resetBasePurchaseForm` para restablecer todos los campos
 * del formulario (`form`), errores (`formErrors`) y flags de control (`purchaseSaved`, etc.).
 *
 * ✅ Este método es útil después de finalizar una compra, al cancelar el proceso
 * o cuando el usuario desea comenzar un nuevo registro desde cero.
 *
 * 🧠 También garantiza que no queden datos residuales del formulario anterior.
 */
  const handlerResetBasePurchase = () => {
    dispatch(resetBasePurchaseForm());
  };

  /**
 * 💳 Establece el código de la condición de pago seleccionada en el formulario.
 *
 * @param code Código de la condición de pago, o `null` si no hay selección.
 *
 * ✅ Llama al reducer `setPaymentConditionCode` para actualizar el estado global.
 */
  const setPaymentConditionCodeFn = (code: string | null) => {
    dispatch(setPaymentConditionCode(code));
  };

  /**
   * 📄 Abre el modal de visualización de PDF de la compra.
   *
   * ✅ Llama al reducer `setOpenPdfModal` para mostrar el modal.
   */
  const openPdfModal = () => {
    dispatch(setOpenPdfModal());
  };

  /**
   * 📄 Cierra el modal de visualización de PDF de la compra.
   *
   * ✅ Llama al reducer `setClosePdfModal` para ocultar el modal.
   */
  const closePdfModal = () => {
    dispatch(setClosePdfModal());
  };

  /**
   * 🧾 Asigna metadatos relacionados a una compra específica.
   *
   * @param id Identificador de la compra que se desea registrar en metadatos.
   *
   * ✅ Llama al reducer `setMeta` para guardar la información en el estado global.
   */
  const setMetaData = (id: number) => {
    dispatch(setMeta({ id }));
  };

  /**
   * 🧹 Limpia los metadatos asociados a compras.
   *
   * ✅ Llama al reducer `clearMeta` para eliminar cualquier referencia previa.
   */
  const clearMetaData = () => {
    dispatch(clearMeta());
  };

  /**
   * 🔽 Disminuye el contador de compras pendientes.
   *
   * ✅ Llama al reducer `decreasePendingPurchaseCount`, normalmente
   * después de completar, anular o finalizar una compra pendiente.
   */
  const decreasePendingCount = () => {
    dispatch(decreasePendingPurchaseCount());
  };

  /**
   * 🗑️ Elimina una compra pendiente de la lista global según su ID.
   *
   * @param id Identificador de la compra a eliminar.
   *
   * ✅ Llama al reducer `removePendingPurchaseById` para depurar la cola de compras pendientes.
   */
  const removePendingById = (id: number) => {
    dispatch(removePendingPurchaseById(id));
  };

  /**
       * 📄 Abre el modal exclusivo de visualización del DTE en PDF.
       *
       * ✅ Este método despacha la acción `setOpenDtePdfModal` del slice de ventas,
       *    cambiando el estado `showDtePdfModal` a `true`.
       *
       * 🔹 Cuándo usarlo:
       *    - Cuando el usuario desea ver la versión oficial del DTE emitido en formato PDF.
       *    - Cuando se desea mostrar el componente `<DtePdfModal />` para una visualización directa sin opción a cambio de tipo.
       *
       * 🛠️ Ejemplo de uso en un componente:
       * ```tsx
       * const { openDtePdfModal } = useSale();
       * <button onClick={openDtePdfModal}>Ver DTE PDF</button>
       * ```
       */
  const openDtePdfModal = () => {
    dispatch(setOpenDtePdfModal());
  };

  /**
   * ❌ Cierra el modal exclusivo de visualización de DTE en PDF.
   *
   * ✅ Este método despacha la acción `setCloseDtePdfModal` del slice de ventas,
   *    cambiando el estado `showDtePdfModal` a `false`.
   *
   * 🔹 Cuándo usarlo:
   *    - Cuando el usuario cierra manualmente el modal de DTE PDF.
   *    - Al finalizar la visualización del documento.
   *
   * 🛠️ Ejemplo de uso en un componente:
   * ```tsx
   * const { closeDtePdfModal } = useSale();
   * <DtePdfModal isOpen={showDtePdfModal} onClose={closeDtePdfModal} />
   * ```
   */
  const closeDtePdfModal = () => {
    dispatch(setCloseDtePdfModal());
  };

  /**
 * 🧹 Limpia el número de documento y reinicia el correlativo solicitado.
 *
 * ✅ Útil cuando se cambia el tipo de documento en el formulario.
 */
  const clearDocumentNumber = () => {
    dispatch(clearDocumentNumberAndCorrelative());
  };


  return {
    form,
    formErrors,


    /**
     * Actualiza un campo individual del formulario de compra.
     *
     * @param field - Nombre del campo a actualizar (clave del formulario).
     * @param value - Valor a asignar al campo.
     */
    updateField: <K extends keyof PurchaseFormValues>(
      field: K,
      value: PurchaseFormValues[K]
    ) => dispatch(updatePurchaseField({ field, value })),


    /**
     * 💵 Actualiza los pagos individuales de la compra.
     *
     * @param updates Objeto parcial con los campos de `IndividualPayments` a modificar.
     * ✅ Llama al reducer `updateIndividualPayments` para aplicar los cambios en el estado global.
     */
    updateIndividualPayments: (updates: Partial<IndividualPayments>) =>
      dispatch(updateIndividualPayments(updates)),

    /**
     * 🧹 Limpia todos los pagos individuales registrados en la compra.
     *
     * ✅ Llama al reducer `clearIndividualPayments` para restablecerlos a su estado inicial.
     */
    clearIndividualPayments: () => dispatch(clearIndividualPayments()),


    /**
   * Agrega uno o varios productos a la compra.
   *
   * @param productOrProducts - Un solo producto (`BaseProduct`) o una lista de productos (`BaseProduct[]`).
   */
    addProduct: (productOrProducts: BaseProduct | BaseProduct[]) => {
      if (Array.isArray(productOrProducts)) {
        productOrProducts.forEach((product) => dispatch(addPurchaseProduct(product)));
      } else {
        dispatch(addPurchaseProduct(productOrProducts));
      }
    },


    /**
     * Elimina un producto del formulario de compra por su ID.
     *
     * @param id - ID del producto a eliminar.
     */
    deleteProduct: (id: number) => dispatch(deletePurchaseProduct(id)),

    /**
     * Actualiza un producto existente en la lista del formulario de compra.
     *
     * @param product - Objeto actualizado del producto (debe extender `BaseProduct`).
     */
    updateProduct: (product: BaseProduct) => dispatch(updatePurchaseProduct(product)),


    /**
     * Limpia completamente el formulario de compra.
     * 
     * 🧹 Restablece todos los campos y errores del formulario a su estado inicial.
     */
    clearForm: () => dispatch(clearPurchaseForm()),

    /**
     * Asigna errores de validación personalizados al formulario.
     *
     * @param errors - Objeto con los errores por campo.
     */
    setErrors: (errors: Record<string, string>) => dispatch(setFormErrors(errors)),

    /**
     * Elimina todos los errores actuales del formulario.
     */
    clearErrors: () => dispatch(clearFormErrors()),

    /**
     * Busca un producto en el formulario por su ID.
     *
     * @param id - ID del producto a buscar.
     * @returns El producto correspondiente o `null` si no se encuentra.
     */
    getProductById: (id: number) => {
      return form.products.find((p) => p.id === id) || null;
    },

    /**
     * Indica si la compra ha sido generada exitosamente.
     * `true` si se guardó correctamente, `false` si hubo error o aún no se ha guardado.
     */
    purchaseSaved,

    /**
   * Indica si el formulario de compra ha sido cargado para edición.
   */
    purchaseLoaded,

    /**
   * Indica si se cargó una compra para visualizar en modo solo lectura.
   */
    purchaseToViewLoaded,


    /**
 * Indica si se cargó una compra en modo de asentamiento de productos.
 */
    purchaseSettlementLoaded,

    /**
  * Obtiene la lista de compras finalizadas para Casa Matriz.
  */
    handlerGetFinalizedPurchases,

    /**
     * Obtiene el total de compras finalizadas para Casa Matriz.
     */
    handlerGetFinalizedTotal,

    /**
     * Obtiene la lista de compras pendientes para Casa Matriz.
     */
    handlerGetPendingPurchases,

    /**
     * Obtiene el total de compras pendientes para Casa Matriz.
     */
    handlerGetPendingTotal,

    /**
     * Total de compras finalizadas (solo Casa Matriz).
     */
    finalizedPurchaseCount,

    /**
     * Lista de compras finalizadas (Casa Matriz).
     */
    finalizedPurchaseList,

    /**
     * Total de compras pendientes (solo Casa Matriz).
     */
    pendingPurchaseCount,

    /**
     * Lista de compras pendientes (Casa Matriz).
     */
    pendingPurchaseList,

    /**
   * Lista de compras filtradas según búsqueda en historial.
   */
    filteredPurchaseList,

    /**
 * Tamaño actual de página para la paginación del historial.
 */
    pageSize,

    /**
 * Información del paginador (total de páginas, elementos, etc.).
 */
    paginator,

    /**
   * Indica si se está enviando una solicitud (bloquea botones en formularios).
   */
    isSubmitting,

    /**
   * 📌 Representa el tipo de documento *activo* usado para renderizar la sección correcta del formulario.
   *
   * ✅ A diferencia de `form.documentType`, este valor NO cambia inmediatamente cuando el usuario modifica el select.
   * ✅ Se actualiza solo cuando:
   *    - Se carga una compra desde el backend.
   *    - Se actualiza el encabezado con éxito (por ejemplo, al cambiar el tipo de documento).
   *
   * 🧠 Esto previene que el formulario cambie de sección mientras el usuario edita el tipo de documento.
   * 🔐 Asegura una experiencia de edición más segura y coherente visualmente.
   */
    activeDocumentType,


    /**
     * 🔢 Indica si ya se solicitó un correlativo para la compra actual.
     *
     * ✅ Es un flag de control útil para evitar múltiples solicitudes al backend.
     */
    correlativeRequested,

    /**
     * 💳 Código actual de la condición de pago seleccionada en el formulario.
     *
     * Puede ser un string con el código o `null` si no se ha definido.
     */
    paymentConditionCode,


    /**
     * ⚙️ Métodos relacionados con la compra
     */

    /**
     * Genera una nueva compra a partir del formulario (solo encabezado).
    */
    handlerGenerate,

    /**
     * Actualiza el encabezado de una compra existente (por ID).
     */
    handlerUpdatePurchaseHeader,

    /**
     * Finaliza la compra actual (la marca como FINALIZED).
     */
    handlerFinalizePurchase,

    /**
     * 📦 Métodos para obtener datos
     */

    /**
     * Obtiene la lista de compras según filtros, paginación y ordenamiento.
     */
    handlerGetFilteredPurchases,

    /**
     * Cancela una compra pendiente (estado DRAFT_CANCELLED).
     */
    handlerCancelPendingPurchase,

    /**
     * Obtiene los datos completos de una compra para visualizar en un modal.
     */
    handlerViewPurchaseById,

    /**
     * Carga una compra por ID para su edición en el formulario.
     */
    handlerEditPurchaseById,

    /**
   * Carga una compra por su ID con información de asentamientos.
   *
   * 🔸 Realiza una solicitud al backend para obtener el detalle completo de la compra, 
   *     incluyendo el progreso de asentamiento por producto.
   * 🔸 Mapea los datos al formato del formulario y los guarda en el estado global.
   * 🔸 Marca el estado como cargado para modo de asentamiento (`purchaseSettlementLoaded = true`).
   * 🔸 Muestra una alerta con el mensaje del backend.
   * 🔸 Redirige al formulario `/purchase-register` para comenzar el proceso de asentamiento.
   * 🔸 Si ocurre un error, muestra una alerta y redirige al historial.
   *
   * @param id - ID de la compra que se desea cargar para asentar productos.
   */
    handlerSettlePurchaseProgressById,

    /**
     * 🧹 Utilidades y helpers
     */

    /**
     * Actualiza el tamaño de página del historial de compras.
     */
    updatePageSize,

    /**
     * Cierra el modal de vista de compra y limpia el formulario.
     */
    handleCloseViewModal,

    /**
     * Limpia la tabla de compras filtradas y su paginador.
     */
    handlerClearFilteredPurchaseList,

    /**
     * 🧹 Restablece todo el estado del formulario de compra como si fuera nuevo.
     * 
     * ✅ Limpia campos, errores y flags de control.
     */
    handlerResetBasePurchase,

    /**
     * 💳 Establece el código de la condición de pago en el formulario.
     */
    setPaymentConditionCodeFn,

    /**
     * 🔢 Obtiene el correlativo de la compra según el tipo de documento.
     */
    handlerGetPurchaseCorrelative,

    /**
     * 👁️ Estado booleano que indica si el modal PDF está visible.
     */
    showPdfModal,

    /**
     * 📄 Abre el modal de visualización de PDF.
     */
    openPdfModal,

    /**
     * 📄 Cierra el modal de visualización de PDF.
     */
    closePdfModal,

    /**
     * 🧾 Asigna metadatos asociados a una compra específica.
     */
    setMetaData,

    /**
     * 🧹 Limpia los metadatos de compras previamente asignados.
     */
    clearMetaData,

    /**
     * 🔽 Reduce el contador global de compras pendientes.
     */
    decreasePendingCount,

    /**
     * 🗑️ Elimina una compra pendiente de la lista según su ID.
     */
    removePendingById,

    showDtePdfModal,

    openDtePdfModal,
    closeDtePdfModal,
    clearDocumentNumber,

    handlerResetPurchaseFormOnly,
  };
};
